import React from 'react'
import PropTypes from 'prop-types'
import {Button, InputNumber, Tooltip} from 'antd'
import {UTILIZATION_WARNING, WORKLOAD_PARAM_RANGES} from '../../constants'
import {formatDecimal} from '../../helpers/format'
import Notice from '../shared/Notice'

/**
 * Vùng Tham số nâng cao (A7) của SCR-M17-001.
 *
 * ⚠️ Hộp cảnh báo A7.7 in ĐỦ nội dung, không rút gọn (§3.1.1). Đây là hộp cảnh báo chứ không phải
 *    ghi chú chữ xám, vì đặt sai tham số Tỷ lệ giờ làm việc hữu ích làm sai toàn bộ số người ở mọi
 *    công đoạn — và sai theo hướng thiếu một cách hệ thống, tức là không ai phát hiện ra.
 *
 * Giá trị ngoài khoảng hợp lệ: viền đỏ, hiện thông báo dưới ô, **không áp dụng giá trị và không
 * tính lại**, giữ giá trị hợp lệ trước đó (§4.7).
 */
function WorkloadParamsPanel({
    params, errors, canSave, onChange, onCommit, saveSlot,
    stages, canSaveStages, stageReworkValue, onStageReworkChange, onStageReworkCommit, onLoadActual,
}) {
    const field = (key, value, onValueChange) => {
        const range = WORKLOAD_PARAM_RANGES[key]
        const invalid = !!errors[key]

        return (
            <div className={`VcParamField ${invalid ? 'VcParamField--invalid' : ''}`} key={key}>
                <label>
                    {key === 'personUtilization' || key === 'machineUtilization' ? (
                        <Tooltip
                            title={key === 'personUtilization'
                                ? UTILIZATION_WARNING.person
                                : UTILIZATION_WARNING.machine}
                        >
                            <span className="VcHasTooltip">{range.label}</span>
                        </Tooltip>
                    ) : range.label}
                </label>
                {/*
                  * `onChange` chỉ đổi giá trị trong phiên xem; `onBlur` / Enter mới ghi xuống hệ
                  * thống. `InputNumber` bắn `onChange` theo từng ký tự, ghi ngay là mỗi chữ số
                  * một lượt PUT vào cấu hình dùng chung của cả nhà máy.
                  */}
                <InputNumber
                    min={range.min}
                    max={range.max}
                    step={range.step}
                    value={value}
                    onChange={onValueChange}
                    onBlur={onCommit}
                    onPressEnter={onCommit}
                />
                {invalid && <div className="VcFieldError">{range.error}</div>}
            </div>
        )
    }

    return (
        <div className="VcParamsPanel">
            <div className="VcParamsGrid">
                {field('shiftsPerDay', params.shiftsPerDay, (value) => onChange({shiftsPerDay: value}))}
                {field('hoursPerShift', params.hoursPerShift, (value) => onChange({hoursPerShift: value}))}
                {field('personUtilization', params.personUtilization, (value) => onChange({personUtilization: value}))}
                {field('machineUtilization', params.machineUtilization, (value) => onChange({machineUtilization: value}))}
                {field('peoplePerMachine', params.peoplePerMachine, (value) => onChange({peoplePerMachine: value}))}
                {field(
                    'reworkRatePercent',
                    Math.round(params.reworkRate * 1000) / 10,
                    // `(value || 0)` sẽ biến ô XOÁ TRẮNG (antd phát `''`) thành 0% — gộp "chưa đặt"
                    // với "không bao giờ làm lại", đúng hai chuyện mà ô tỷ lệ theo công đoạn ở
                    // `WorkloadPage.commitReworkRate` cố ý phân biệt. Ở đây 0 lại nằm TRONG khoảng
                    // hợp lệ nên `handleParamChange` áp luôn, và nếu đang tích "Lưu cho lần sau"
                    // thì ghi `reworkRate: 0` thành cấu hình dùng chung cả nhà máy.
                    // Bỏ trống ⇒ để `undefined` cho `handleParamChange` bắt là ngoài khoảng và
                    // giữ nguyên giá trị hợp lệ trước đó.
                    (value) => onChange({
                        reworkRate: value === '' || value === null || value === undefined
                            ? undefined
                            : value / 100,
                    })
                )}
            </div>

            {/*
              * Tỷ lệ làm lại RIÊNG của từng công đoạn.
              *
              * Đặt ở đây chứ không phải thêm cột vào bảng kết quả, vì hai lý do. Bảng đã rộng 1800px
              * và phải cuộn ngang — một ô nhập nằm ngoài tầm mắt thì coi như không có. Và §5 của
              * FAC-M17-SRS-002 chốt bảng kết quả giữ đúng 16 cột, không thêm không bớt: bảng để ĐỌC
              * số, khu tham số để ĐẶT cấu hình.
              *
              * Bỏ trống = dùng tỷ lệ mặc định ở trên (ô hiện sẵn giá trị đó làm gợi ý). Đặt 0 là một
              * khai báo khác hẳn: công đoạn này không bao giờ phải làm lại.
              */}
            {stages.length > 0 && (
                <div className="VcStageRework">
                    <div className="VcStageRework__title">
                        <span>
                            Tỷ lệ làm lại theo công đoạn (%) — bỏ trống thì dùng mặc định{' '}
                            {formatDecimal(params.reworkRate * 100, 1)}
                        </span>
                        {canSaveStages && onLoadActual && (
                            <Button size="small" type="link" onClick={onLoadActual}>
                                Nạp từ dữ liệu thực tế
                            </Button>
                        )}
                    </div>
                    <div className="VcStageRework__grid">
                        {stages.map((stage) => (
                            <label className="VcStageRework__cell" key={stage.stageCode}>
                                <span className="VcStageRework__name">{stage.name}</span>
                                <InputNumber
                                    size="small"
                                    min={0}
                                    max={50}
                                    step={0.5}
                                    disabled={!canSaveStages}
                                    placeholder={formatDecimal(params.reworkRate * 100, 1)}
                                    value={stageReworkValue(stage)}
                                    onChange={(value) => onStageReworkChange(stage.stageCode, value)}
                                    onBlur={() => onStageReworkCommit(stage)}
                                    onPressEnter={() => onStageReworkCommit(stage)}
                                />
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/*
              * Khối lưu cấu hình đặt NGAY DƯỚI lưới input, trước hộp cảnh báo dài.
              *
              * Hộp cảnh báo về tỷ lệ giờ hữu ích chiếm gần nửa màn hình; để nút lưu sau nó thì người
              * vừa sửa xong một ô phải cuộn qua một đoạn văn mới tới được nút — và nhiều người sẽ
              * tưởng sửa xong là tự lưu.
              */}
            {saveSlot}

            <Notice level="warning">
                {UTILIZATION_WARNING.full.map((paragraph, index) => (
                    <div key={index} className={index ? 'mt-2' : ''}>{paragraph}</div>
                ))}
            </Notice>

            <div className="VcHint">
                Tỷ lệ làm lại ở đây là giá trị MẶC ĐỊNH, chỉ áp cho công đoạn nào chưa đặt riêng — từng
                công đoạn đặt được tỷ lệ của mình ngay trên bảng kết quả, bỏ trống thì dùng giá trị này.
                Số người và số máy đang có cũng nhập trong bảng, đều lưu dùng chung cho cả nhà máy.
                {!canSave && (
                    <span className="VcWarning">
                        {' '}Bạn không có quyền lưu tham số — đổi được để xem thử, đóng màn hình là mất.
                    </span>
                )}
            </div>
        </div>
    )
}

WorkloadParamsPanel.propTypes = {
    params: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    canSave: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onCommit: PropTypes.func.isRequired,
    /** Khối "Lưu cấu hình" do trang cha dựng — panel chỉ quyết định nó nằm ở đâu. */
    saveSlot: PropTypes.node,
    /** Danh sách công đoạn của báo cáo hiện tại — dùng để dựng lưới tỷ lệ làm lại riêng. */
    stages: PropTypes.array,
    canSaveStages: PropTypes.bool,
    stageReworkValue: PropTypes.func,
    onStageReworkChange: PropTypes.func,
    onStageReworkCommit: PropTypes.func,
    /** Mở hộp thoại nạp tỷ lệ làm lại đo được từ sản lượng thực tế. */
    onLoadActual: PropTypes.func,
}

WorkloadParamsPanel.defaultProps = {
    saveSlot: null,
    stages: [],
    canSaveStages: false,
    stageReworkValue: () => undefined,
    onStageReworkChange: () => undefined,
    onStageReworkCommit: () => undefined,
    onLoadActual: null,
}

export default WorkloadParamsPanel
